/* ═══════════════════════════════════════════════════════════════
   SearchableSelect — keyed (value/label) searchable single-select.
   Used anywhere the user picks one item from a list. Replaces
   plain <select> per firm UX policy (no dropdown-only controls).

   The menu renders in a portal on <body> (fixed, viewport-clamped,
   flips above the trigger when there's no room below) so it is NEVER
   clipped by panel overflow or transformed ancestors — contextual UI
   always lands on top of everything.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

export interface SelectOption {
  value: string;
  label: string;
  hint?: string; // optional secondary text shown in the row
  group?: string; // optional group heading to show above item
}

export interface SearchableSelectProps {
  value: string; // current value (option.value)
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string; // shown when nothing selected
  searchPlaceholder?: string; // shown in the search input
  clearable?: boolean; // show "Clear selection" (default true)
  disabled?: boolean;
  style?: React.CSSProperties; // applied to trigger
  noResultsText?: string;
  autoOpenOnFocus?: boolean; // opens when focused via keyboard
}

const MENU_MAX_H = 280;
const MENU_GAP = 4;

export function SearchableSelect({
  value,
  options,
  onChange,
  placeholder = '— Select —',
  searchPlaceholder = 'Search…',
  clearable = true,
  disabled = false,
  style,
  noResultsText = 'No matches',
  autoOpenOnFocus = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [focusIdx, setFocusIdx] = useState(0);
  const [pos, setPos] = useState<{ left: number; top: number; width: number; maxH: number } | null>(
    null
  );
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  // Position the portal menu off the trigger: below by default, above when the
  // viewport has no room, clamped horizontally. Fixed-position + portal = never
  // clipped by overflow or skewed by transformed ancestors.
  const place = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom - MENU_GAP - 8;
    const above = r.top - MENU_GAP - 8;
    const openUp = below < 180 && above > below;
    const maxH = Math.max(140, Math.min(MENU_MAX_H, openUp ? above : below));
    const width = Math.max(r.width, 200);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    setPos({
      left,
      width,
      maxH,
      top: openUp ? Math.max(8, r.top - MENU_GAP - maxH) : r.bottom + MENU_GAP,
    });
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setFocusIdx(0);
    setTimeout(() => inputRef.current?.focus(), 0);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    };
    // Scrolling/resizing the page under a fixed menu detaches it — close instead.
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return; // menu's own scroll is fine
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const filtered = query
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(query.toLowerCase()) ||
          (o.hint && o.hint.toLowerCase().includes(query.toLowerCase()))
      )
    : options;

  function commit(v: string) {
    onChange(v);
    setOpen(false);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[focusIdx];
      if (opt) commit(opt.value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  const trigger: React.CSSProperties = {
    width: '100%',
    padding: '7px 10px',
    borderRadius: 5,
    border: '1px solid var(--bd)',
    background: disabled ? 'var(--sa)' : 'var(--ib)',
    color: 'var(--tx)',
    fontSize: 13,
    cursor: disabled ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 34,
    ...style,
  };

  // Group-aware render: if any option has a `group`, break by group in filtered list
  const hasGroups = options.some((o) => o.group);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (
            !open &&
            (e.key === 'Enter' || e.key === ' ' || (autoOpenOnFocus && e.key === 'ArrowDown'))
          ) {
            e.preventDefault();
            setOpen(true);
          } else if (open) onKey(e);
        }}
        style={trigger}
      >
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: selected ? 'var(--tx)' : 'var(--tm)',
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden
          style={{
            marginLeft: 6,
            flexShrink: 0,
            color: 'var(--tm)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform .15s ease',
          }}
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {open &&
        !disabled &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.top,
              width: pos.width,
              zIndex: 2147483000,
              background: 'var(--sf)',
              border: '1px solid var(--bd)',
              borderRadius: 6,
              boxShadow: 'var(--shadow-lg)',
              maxHeight: pos.maxH,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--bd)' }}>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setFocusIdx(0);
                }}
                onKeyDown={onKey}
                placeholder={searchPlaceholder}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  borderRadius: 4,
                  border: '1px solid var(--bd)',
                  background: 'var(--ib)',
                  color: 'var(--tx)',
                  fontSize: 12,
                  outline: 'none',
                }}
              />
            </div>
            <div style={{ overflow: 'auto', flex: 1 }}>
              {clearable && selected && (
                <div
                  onClick={() => commit('')}
                  style={{
                    padding: '7px 12px',
                    fontSize: 12,
                    color: 'var(--tm)',
                    cursor: 'pointer',
                    fontStyle: 'italic',
                    borderBottom: '1px solid var(--bd)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  Clear selection
                </div>
              )}
              {filtered.length === 0 && (
                <div
                  style={{
                    padding: '16px 12px',
                    fontSize: 12,
                    color: 'var(--tm)',
                    textAlign: 'center',
                  }}
                >
                  {noResultsText}
                </div>
              )}
              {(() => {
                let lastGroup: string | undefined;
                return filtered.map((o, i) => {
                  const showGroup = hasGroups && o.group !== lastGroup;
                  lastGroup = o.group;
                  const isSel = o.value === value;
                  const isFocused = i === focusIdx;
                  return (
                    <React.Fragment key={o.value || `__${i}`}>
                      {showGroup && o.group && (
                        <div
                          style={{
                            padding: '8px 12px 4px',
                            fontSize: 10,
                            fontWeight: 700,
                            color: 'var(--tm)',
                            textTransform: 'uppercase',
                            letterSpacing: '.05em',
                          }}
                        >
                          {o.group}
                        </div>
                      )}
                      <div
                        role="option"
                        aria-selected={isSel}
                        onMouseEnter={() => setFocusIdx(i)}
                        onClick={() => commit(o.value)}
                        style={{
                          padding: '7px 12px',
                          fontSize: 12,
                          cursor: 'pointer',
                          fontWeight: isSel ? 600 : 400,
                          color: isSel ? 'var(--ac)' : 'var(--tx)',
                          background: isFocused
                            ? 'var(--sa)'
                            : isSel
                              ? 'rgba(var(--acr),0.06)'
                              : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {o.label}
                        </span>
                        {o.hint && (
                          <span style={{ fontSize: 10, color: 'var(--tm)', flexShrink: 0 }}>
                            {o.hint}
                          </span>
                        )}
                      </div>
                    </React.Fragment>
                  );
                });
              })()}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default SearchableSelect;
